import { TABLE_DIVIDER_WIDTH } from '@/features/shared/components/tableSystem/tableConstants';
import { resolveTableDividerColor } from '@/features/shared/components/tableSystem/tableDividerSx';
import { componentSizes } from '@/theme/componentSizes';
import { TABLE_CELL_TOP_PADDING } from '@/ui/composites/table/cells/sharedCellContent';
import { Box, Skeleton } from '@/ui/primitives';
import { s } from '../../../theme/tokens';
import { resolveGridColumnAlignment, wrapDataGridColumns } from './wrapDataGridColumns';
import type { SpacingToken } from '@/theme/tokens';
import type { GridColDef, GridRowId, GridValidRowModel, SxProps, Theme } from '@/ui/primitives';
import type { JSX } from 'react';

export type LoadingRow = { id: string; __loading: true };

const skeletonWidthSteps = [38, 44, 51, 58, 65, 72, 79] as const;
const TABLE_SKELETON_HEIGHT = 14;
const TABLE_BOTTOM_PADDING = '10px';
const TABLE_SKELETON_TOP_PADDING = `calc(${TABLE_CELL_TOP_PADDING.standard} + 2px)`;
const TABLE_SKELETON_ACTION_TOP_PADDING = `calc(${TABLE_CELL_TOP_PADDING.standard} + 4px)`;
const TABLE_SKELETON_TEXT_LINE_GAP = '7px';
const TABLE_SKELETON_STACK_GAP = '6px';
const TABLE_SKELETON_TEXT_PRIMARY_WIDTH = '88%';
const TABLE_SKELETON_TEXT_SECONDARY_WIDTH = '72%';
const TABLE_SKELETON_STATUS_PILL_WIDTH = '72px';
const TABLE_SKELETON_STATUS_META_WIDTH = '44px';
const TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH = '56px';
const TABLE_SKELETON_ACTION_SIZE = '18px';
const TABLE_SKELETON_RADIUS = '999px';
const TABLE_SKELETON_STATUS_INDENT = '2px';
const TABLE_SKELETON_ICON_SIZE = '14px';
const TABLE_SKELETON_BREAKDOWN_TOTAL_WIDTH = '54px';
const TABLE_SKELETON_BREAKDOWN_STATUS_WIDTH = '92px';
const TABLE_SKELETON_BREAKDOWN_DETAIL_WIDTH = '76px';
const TABLE_SKELETON_RUN_LATEST_WIDTH = '56px';
const TABLE_SKELETON_META_PRIMARY_WIDTH = '84px';
const TABLE_SKELETON_META_SECONDARY_WIDTH = '96px';
const TABLE_SKELETON_META_TERTIARY_WIDTH = '72px';
const TABLE_SKELETON_META_QUATERNARY_WIDTH = '88px';
const TABLE_SKELETON_METRIC_PRIMARY_WIDTH = '64px';
const TABLE_SKELETON_METRIC_SECONDARY_WIDTH = '56px';
const TABLE_SKELETON_METRICS_PASS_RATE_WIDTH = '92px';
const TABLE_SKELETON_METRICS_FREQUENCY_WIDTH = '104px';
const TABLE_SKELETON_METRICS_DURATION_WIDTH = '132px';
const TABLE_SKELETON_METRICS_FLAKE_SCORE_WIDTH = '108px';
const TABLE_SKELETON_HEADER_LEFT_OFFSET = '6px';
const TABLE_SKELETON_HEADER_TOP_OFFSET = '2px';
const TABLE_SKELETON_ICON_TEXT_GAP = '6px';
const TABLE_SKELETON_HEADER_MIN_WIDTH = 24;
const TABLE_SKELETON_HEADER_MAX_WIDTH = 132;
const TABLE_SKELETON_HEADER_LETTER_WIDTH = 7;
const TABLE_SKELETON_HEADER_SPACE_WIDTH = 4;
const TABLE_SKELETON_HEADER_INLINE_PADDING = 10;

const skeletonColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  boxSizing: 'border-box',
  paddingBottom: TABLE_BOTTOM_PADDING,
} as const;

const resolveHeaderSkeletonMargins = (
  justifyContent: 'flex-start' | 'center' | 'flex-end',
): { ml?: string; mr?: string } => {
  if (justifyContent === 'center') {
    return { ml: 'auto', mr: 'auto' };
  }
  if (justifyContent === 'flex-end') {
    return { ml: 'auto', mr: '0px' };
  }
  return { ml: TABLE_SKELETON_HEADER_LEFT_OFFSET, mr: 'auto' };
};

const hashSkeletonKey = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const resolveCellSkeletonWidth = (params: { id: GridRowId; field: string }): string => {
  const key = `${String(params.id)}:${params.field}`;
  const width = skeletonWidthSteps[hashSkeletonKey(key) % skeletonWidthSteps.length];
  return `${width}%`;
};

const resolveBreakdownSkeletonWidths = (): string[] => {
  return [
    TABLE_SKELETON_BREAKDOWN_TOTAL_WIDTH,
    TABLE_SKELETON_BREAKDOWN_STATUS_WIDTH,
    TABLE_SKELETON_BREAKDOWN_DETAIL_WIDTH,
  ];
};

const resolveHeaderNameSkeletonWidth = (headerName: string): string => {
  const textWidth = Array.from(headerName).reduce((totalWidth, character) => {
    const characterWidth =
      character.trim().length === 0
        ? TABLE_SKELETON_HEADER_SPACE_WIDTH
        : TABLE_SKELETON_HEADER_LETTER_WIDTH;

    return totalWidth + characterWidth;
  }, TABLE_SKELETON_HEADER_INLINE_PADDING);

  return `${Math.min(
    TABLE_SKELETON_HEADER_MAX_WIDTH,
    Math.max(TABLE_SKELETON_HEADER_MIN_WIDTH, textWidth),
  )}px`;
};

const resolveHeaderSkeletonWidth = (args: {
  field: string;
  headerName: string | undefined;
}): string => {
  const rawHeaderName = args.headerName ?? args.field;
  const headerName = rawHeaderName.trim();

  if (headerName.length > 0) {
    return resolveHeaderNameSkeletonWidth(headerName);
  }

  return '0px';
};

const resolveMeasuredSkeletonRowHeight = (
  rowId: GridRowId,
  rowHeights: number[] | undefined,
): number | undefined => {
  if (rowHeights === undefined) return undefined;
  const rowIdText = String(rowId);
  if (!rowIdText.startsWith('loading-')) return undefined;
  const rowIndex = Number.parseInt(rowIdText.replace('loading-', ''), 10);
  return Number.isFinite(rowIndex) ? rowHeights[rowIndex] : undefined;
};

const hasCellClass = (cellClassName: string | undefined, className: string): boolean =>
  cellClassName?.split(/\s+/).includes(className) ?? false;

const isCloneStructuredCell = (field: string, cellClassName: string | undefined): boolean =>
  hasCellClass(cellClassName, 'table-name-cell') ||
  hasCellClass(cellClassName, 'table-run-link-cell') ||
  hasCellClass(cellClassName, 'table-latest-meta-cell') ||
  hasCellClass(cellClassName, 'table-status-meta-cell') ||
  hasCellClass(cellClassName, 'table-breakdown-cell') ||
  hasCellClass(cellClassName, 'table-meta-details-cell') ||
  hasCellClass(cellClassName, 'table-metric-cell') ||
  field === 'latestRunStatus' ||
  field === 'testLatestRunBreakdown' ||
  field === 'testStatuses' ||
  field === 'suiteRunPassed' ||
  field === 'testRunPassed' ||
  field === 'suites' ||
  field === 'tests' ||
  field === 'metricsSummary' ||
  field === 'actions';

const renderIconTextLineSkeleton = (args: {
  textWidth: string;
  topMargin?: string;
  withIcon?: boolean;
}): JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: args.withIcon === false ? '0px' : TABLE_SKELETON_ICON_TEXT_GAP,
      mt: args.topMargin ?? '0px',
      width: '100%',
      minWidth: 0,
      pl: TABLE_SKELETON_STATUS_INDENT,
    }}
  >
    {args.withIcon === false ? null : (
      <Skeleton
        variant="circular"
        width={TABLE_SKELETON_ICON_SIZE}
        height={TABLE_SKELETON_ICON_SIZE}
        sx={{ transform: 'none', flexShrink: 0 }}
      />
    )}
    <Skeleton
      variant="rounded"
      width={args.textWidth}
      sx={{
        height: TABLE_SKELETON_HEIGHT,
        transform: 'none',
        borderRadius: TABLE_SKELETON_RADIUS,
        maxWidth:
          args.withIcon === false
            ? '100%'
            : `calc(100% - ${TABLE_SKELETON_ICON_SIZE} - ${TABLE_SKELETON_ICON_TEXT_GAP})`,
      }}
    />
  </Box>
);

const renderTextLineSkeleton = (args: { textWidth: string; topMargin?: string }): JSX.Element => (
  <Box sx={{ width: '100%', minWidth: 0, mt: args.topMargin ?? '0px' }}>
    <Skeleton
      variant="rounded"
      width={args.textWidth}
      sx={{
        height: TABLE_SKELETON_HEIGHT,
        transform: 'none',
        borderRadius: TABLE_SKELETON_RADIUS,
        maxWidth: '100%',
      }}
    />
  </Box>
);

const renderStatusLineSkeletons = (): JSX.Element[] =>
  [
    TABLE_SKELETON_STATUS_PILL_WIDTH,
    TABLE_SKELETON_STATUS_META_WIDTH,
    TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH,
  ].map((textWidth, index) => (
    <Box key={textWidth}>
      {renderTextLineSkeleton({
        textWidth,
        topMargin: index === 0 ? '0px' : TABLE_SKELETON_STACK_GAP,
      })}
    </Box>
  ));

const renderCloneCellSkeleton = (
  params: { id: GridRowId; field: string },
  options: {
    field: string;
    cellClassName: string | undefined;
    rowHeight?: number;
    rowHeights?: number[];
  },
): JSX.Element => {
  const resolvedRowHeight = resolveMeasuredSkeletonRowHeight(params.id, options.rowHeights);
  const effectiveRowHeight = resolvedRowHeight ?? options.rowHeight;
  const fixedRowHeightSx =
    typeof effectiveRowHeight === 'number'
      ? {
          minHeight: `${effectiveRowHeight}px`,
          maxHeight: `${effectiveRowHeight}px`,
          height: `${effectiveRowHeight}px`,
        }
      : null;

  if (hasCellClass(options.cellClassName, 'table-name-cell')) {
    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
          },
          fixedRowHeightSx,
        ]}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: TABLE_SKELETON_TEXT_LINE_GAP,
            width: '100%',
          }}
        >
          <Skeleton
            variant="rounded"
            width={TABLE_SKELETON_TEXT_PRIMARY_WIDTH}
            sx={{
              height: TABLE_SKELETON_HEIGHT,
              transform: 'none',
              borderRadius: TABLE_SKELETON_RADIUS,
            }}
          />
          <Skeleton
            variant="rounded"
            width={TABLE_SKELETON_TEXT_SECONDARY_WIDTH}
            sx={{
              height: TABLE_SKELETON_HEIGHT,
              transform: 'none',
              borderRadius: TABLE_SKELETON_RADIUS,
            }}
          />
        </Box>
      </Box>
    );
  }

  if (hasCellClass(options.cellClassName, 'table-run-link-cell')) {
    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            pl: TABLE_SKELETON_STATUS_INDENT,
            gap: TABLE_SKELETON_STACK_GAP,
          },
          fixedRowHeightSx,
        ]}
      >
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_RUN_LATEST_WIDTH}
          height={TABLE_SKELETON_HEIGHT}
          sx={{ transform: 'none', borderRadius: TABLE_SKELETON_RADIUS }}
        />
      </Box>
    );
  }

  if (
    options.field === 'latestRunStatus' ||
    hasCellClass(options.cellClassName, 'table-latest-meta-cell')
  ) {
    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            pl: TABLE_SKELETON_STATUS_INDENT,
          },
          fixedRowHeightSx,
        ]}
      >
        {renderStatusLineSkeletons()}
      </Box>
    );
  }

  if (options.field === 'metricsSummary') {
    const lineWidths = [
      TABLE_SKELETON_METRICS_PASS_RATE_WIDTH,
      TABLE_SKELETON_METRICS_FREQUENCY_WIDTH,
      TABLE_SKELETON_METRICS_DURATION_WIDTH,
      TABLE_SKELETON_METRICS_FLAKE_SCORE_WIDTH,
    ] as const;

    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            gap: TABLE_SKELETON_STACK_GAP,
          },
          fixedRowHeightSx,
        ]}
      >
        {lineWidths.map((textWidth, index) => (
          <Box key={index}>{renderIconTextLineSkeleton({ textWidth, withIcon: false })}</Box>
        ))}
      </Box>
    );
  }

  if (
    hasCellClass(options.cellClassName, 'table-breakdown-cell') ||
    options.field === 'testLatestRunBreakdown' ||
    options.field === 'testStatuses' ||
    options.field === 'suiteRunPassed' ||
    options.field === 'testRunPassed' ||
    options.field === 'suites' ||
    options.field === 'tests'
  ) {
    const lineWidths = resolveBreakdownSkeletonWidths();

    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            gap: TABLE_SKELETON_STACK_GAP,
          },
          fixedRowHeightSx,
        ]}
      >
        {lineWidths.map((textWidth, index) => (
          <Box key={index}>{renderIconTextLineSkeleton({ textWidth, withIcon: false })}</Box>
        ))}
      </Box>
    );
  }

  if (hasCellClass(options.cellClassName, 'table-meta-details-cell')) {
    const lineWidths =
      options.field === 'error' || options.field === 'output'
        ? [TABLE_SKELETON_META_PRIMARY_WIDTH]
        : hasCellClass(options.cellClassName, 'table-meta-details-four-line-cell')
          ? [
              TABLE_SKELETON_META_PRIMARY_WIDTH,
              TABLE_SKELETON_META_SECONDARY_WIDTH,
              TABLE_SKELETON_META_TERTIARY_WIDTH,
              TABLE_SKELETON_META_QUATERNARY_WIDTH,
            ]
          : [
              TABLE_SKELETON_META_PRIMARY_WIDTH,
              TABLE_SKELETON_META_SECONDARY_WIDTH,
              TABLE_SKELETON_META_TERTIARY_WIDTH,
            ];

    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            gap: TABLE_SKELETON_TEXT_LINE_GAP,
          },
          fixedRowHeightSx,
        ]}
      >
        {lineWidths.map((textWidth, index) => (
          <Box key={index}>{renderIconTextLineSkeleton({ textWidth, withIcon: false })}</Box>
        ))}
      </Box>
    );
  }

  if (hasCellClass(options.cellClassName, 'table-status-meta-cell')) {
    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            pl: TABLE_SKELETON_STATUS_INDENT,
          },
          fixedRowHeightSx,
        ]}
      >
        {renderStatusLineSkeletons()}
      </Box>
    );
  }

  if (options.field === 'actions') {
    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_ACTION_TOP_PADDING,
            alignItems: 'center',
            justifyContent: 'flex-start',
          },
          fixedRowHeightSx,
        ]}
      >
        <Skeleton
          variant="circular"
          width={TABLE_SKELETON_ACTION_SIZE}
          height={TABLE_SKELETON_ACTION_SIZE}
          sx={{
            transform: 'none',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  if (hasCellClass(options.cellClassName, 'table-metric-cell')) {
    return (
      <Box
        sx={[
          skeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            gap: TABLE_SKELETON_TEXT_LINE_GAP,
          },
          fixedRowHeightSx,
        ]}
      >
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_METRIC_PRIMARY_WIDTH}
          sx={{
            height: TABLE_SKELETON_HEIGHT,
            transform: 'none',
            maxWidth: '100%',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_METRIC_SECONDARY_WIDTH}
          sx={{
            height: TABLE_SKELETON_HEIGHT,
            transform: 'none',
            maxWidth: '100%',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={[
        skeletonColumnSx,
        {
          pt: TABLE_SKELETON_TOP_PADDING,
        },
        fixedRowHeightSx,
      ]}
    >
      <Skeleton
        variant="rounded"
        width={resolveCellSkeletonWidth(params)}
        sx={{
          height: componentSizes.dataGrid.skeletonHeight,
          transform: 'none',
          maxWidth: '100%',
          borderRadius: TABLE_SKELETON_RADIUS,
        }}
      />
    </Box>
  );
};

const resolvePadding = (token: SpacingToken): string | number => {
  switch (token) {
    case 'none':
      return 0;
    case 'xxxs':
      return s('xxs');
    case 'xxs':
    case 'xs':
      return s('xs');
    case 'sm':
      return s('sm');
    case 'md':
      return s('md');
    case 'lg':
      return s('lg');
    case 'xl':
      return s('xl');
    case '2xl':
      return s('2xl');
    case '3xl':
      return s('3xl');
    case '4xl':
      return s('4xl');
    case '5xl':
      return s('5xl');
    case 'gutter':
      return s('gutter');
    case 'section':
      return s('section');
    default:
      return s('3xl');
  }
};

export const buildSkeletonDataGridColumns = <T extends GridValidRowModel>({
  columns,
  cellPaddingY,
  rowHeight,
  rowHeights,
}: {
  columns: Array<GridColDef<T>>;
  cellPaddingY?: SpacingToken;
  rowHeight?: number;
  rowHeights?: number[];
}): Array<GridColDef<T>> => {
  const skeletonColumns = columns.map((column) => {
    const { align, headerAlign } = resolveGridColumnAlignment(column);
    const resolvedCellClassName =
      typeof column.cellClassName === 'string' ? column.cellClassName : undefined;
    const isStructuredCloneCell = isCloneStructuredCell(column.field, resolvedCellClassName);
    const cellJustifyContent =
      align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
    const headerJustifyContent =
      column.field === 'actions' ? 'center' : headerAlign === 'right' ? 'flex-end' : 'flex-start';
    const fixedCellHeightSx =
      typeof rowHeight === 'number'
        ? {
            minHeight: `${rowHeight}px`,
            maxHeight: `${rowHeight}px`,
            height: `${rowHeight}px`,
          }
        : null;

    return {
      ...column,
      headerAlign: column.field === 'actions' ? 'center' : 'left',
      renderHeader: () => {
        if (column.field === 'actions') {
          return <Box sx={{ width: '100%', height: '100%' }} />;
        }
        const headerSkeletonWidth = resolveHeaderSkeletonWidth({
          field: column.field,
          headerName: column.headerName,
        });
        const headerSkeletonMargins = resolveHeaderSkeletonMargins(headerJustifyContent);
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: headerJustifyContent,
              height: '100%',
              width: '100%',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Skeleton
              variant="rounded"
              width={headerSkeletonWidth}
              sx={{
                height: componentSizes.dataGrid.skeletonHeight,
                transform: 'none',
                maxWidth: '100%',
                borderRadius: TABLE_SKELETON_RADIUS,
                mt: TABLE_SKELETON_HEADER_TOP_OFFSET,
                ...headerSkeletonMargins,
              }}
            />
          </Box>
        );
      },
      renderCell: (params) => {
        const measuredRowHeight = resolveMeasuredSkeletonRowHeight(params.id, rowHeights);
        const effectiveCellHeightSx =
          typeof measuredRowHeight === 'number'
            ? {
                minHeight: `${measuredRowHeight}px`,
                maxHeight: `${measuredRowHeight}px`,
                height: `${measuredRowHeight}px`,
              }
            : fixedCellHeightSx;

        return (
          <Box
            sx={[
              {
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent:
                  column.field === 'actions'
                    ? 'center'
                    : isStructuredCloneCell
                      ? 'flex-start'
                      : cellJustifyContent,
                height: '100%',
                width: '100%',
                flex: 1,
                minWidth: 0,
                overflow: isStructuredCloneCell ? 'visible' : 'hidden',
              },
              effectiveCellHeightSx,
            ]}
          >
            {renderCloneCellSkeleton(params, {
              field: column.field,
              cellClassName: resolvedCellClassName,
              rowHeight,
              rowHeights,
            })}
          </Box>
        );
      },
    };
  }) as Array<GridColDef<T>>;

  return wrapDataGridColumns(skeletonColumns, {
    getBaseCellSx: () => (cellPaddingY ? { py: resolvePadding(cellPaddingY) } : undefined),
  });
};

export const skeletonDataGridBaseSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  '& .MuiDataGrid-footerContainer': {
    borderTop: (muiTheme) => `${TABLE_DIVIDER_WIDTH}px solid ${resolveTableDividerColor(muiTheme)}`,
  },
};
